

const firstTruthy = (...readers) => readers.reduce((result, read) => result || read(), undefined);
const everyTruthy = (...readers) => readers.reduce((result, read) => result && read(), true);
const firstDefined = (...readers) => readers.reduce((result, read) => result ?? read(), undefined);function resolveObject(value) {
  return value && typeof value === 'object' && !Array.isArray(value) ? value : {};
}

function resolveArray(value) {
  return Array.isArray(value) ? value : [];
}

function text(value) {
  return String(value ?? '').trim();
}

function revisionMatchesTab(revision = {}, tabKey = '') {
  const targetTab = text(tabKey) || 'requirement';
  const activeTab = text(resolveObject(revision).active_tab) || 'requirement';
  return activeTab === targetTab;
}

function revisionKey(revision = {}, index = 0) {
  const item = resolveObject(revision);
  const stableKey = [
    text(item.version),
    firstTruthy(() => (text(item.active_tab)), () => ('requirement')),
    text(firstTruthy(() => (item.created_at), () => (item.updated_at), () => (item.timestamp))),
    text(firstTruthy(() => (item.source_kind), () => (item.diff))),
    text(item.content),
  ].join(':');
  return firstTruthy(() => (text(item.revision_id)), () => (text(stableKey)), () => (`revision:${index}`));
}

function isAcceptedRevision(revision = {}) {
  const item = resolveObject(revision);
  return item.accepted === true
    || text(item.status) === 'accepted'
    || Boolean(text(item.accepted_at));
}

function hasBackendRollbackCapability(revision = {}) {
  const item = resolveObject(revision);
  const action = resolveObject(item.action);
  return item.can_rollback === true || action.enabled === true;
}

function changeSummary(revision = {}) {
  const item = resolveObject(revision);
  const changeSet = resolveObject(item.change_set);
  return text(changeSet.summary) || text(item.diff);
}

function diffPreview(changeSet = {}) {
  const item = resolveObject(changeSet);
  return {
    before_chars: Number(item.before_chars || 0),
    after_chars: Number(item.after_chars || 0),
    before_excerpt: text(item.before_excerpt),
    after_excerpt: text(item.after_excerpt),
  };
}

function revisionControl(value = {}) {
  const item = resolveObject(value);
  return {
    action_key: text(item.action_key) || 'rollback_artifact_revision',
    active_tab: text(item.active_tab) || 'requirement',
    content: String(item.content || ''),
    enabled: item.enabled === true,
    label: text(item.label),
    revision_id: text(item.revision_id),
    revision_key: text(item.revision_key),
    version: item.version,
  };
}

function revisionControlMatchesEntry(control = {}, entry = {}) {
  if (everyTruthy(() => (control.revision_id), () => (control.revision_id === entry.revision_id))) {
    return true;
  }
  if (everyTruthy(() => (control.revision_key), () => ([entry.key, entry.revision_id].includes(control.revision_key)))) {
    return true;
  }
  const controlVersion = text(control.version);
  if (everyTruthy(() => (controlVersion), () => (controlVersion === text(entry.version)), () => (control.active_tab === entry.active_tab))) {
    return true;
  }
  return Boolean(everyTruthy(() => (control.content), () => (control.content === entry.content), () => (control.active_tab === entry.active_tab)));
}

function revisionEntryFromControl(control = {}, revisionEntries = []) {
  const matchedEntry = firstTruthy(() => (revisionEntries.find((entry) => revisionControlMatchesEntry(control, entry))), () => ({}));
  const content = String(firstTruthy(() => (control.content), () => (matchedEntry.content), () => ('')));
  const enabled = everyTruthy(() => (control.enabled === true), () => (Boolean(text(content))));
  const label = firstTruthy(() => (control.label), () => (matchedEntry.label), () => ('历史版本'));
  return {
    ...matchedEntry,
    active_tab: firstTruthy(() => (control.active_tab), () => (matchedEntry.active_tab), () => ('requirement')),
    can_rollback: enabled,
    content,
    enabled,
    key: firstTruthy(() => (control.revision_key), () => (matchedEntry.key), () => (control.revision_id)),
    label,
    revision_id: firstTruthy(() => (control.revision_id), () => (matchedEntry.revision_id), () => ('')),
    version: firstDefined(() => (control.version), () => (matchedEntry.version)),
    action: {
      ...resolveObject(matchedEntry.action),
      action_key: control.action_key,
      active_tab: firstTruthy(() => (control.active_tab), () => (matchedEntry.active_tab), () => ('requirement')),
      content,
      enabled,
      label,
      revision_id: firstTruthy(() => (control.revision_id), () => (matchedEntry.revision_id), () => ('')),
      revision_key: firstTruthy(() => (control.revision_key), () => (matchedEntry.key), () => (control.revision_id)),
    },
  };
}

function revisionOrder(revision = {}, index = 0) {
  const version = Number(resolveObject(revision).version);
  return Number.isFinite(version) ? version : index + 1;
}

function diffView(options) {
  const { previousRevision, revision } = { previousRevision: {}, revision: {}, ...Object.fromEntries(Object.entries(Object(options)).filter(([, value]) => value !== undefined)) };
  const item = resolveObject(revision);
  const changeSet = resolveObject(item.change_set);
  const beforeContent = String(
    firstTruthy(() => (changeSet.before_content), () => (resolveObject(previousRevision).content), () => (changeSet.before_excerpt), () => (''))
  ).trim();
  const afterContent = String(
    firstTruthy(() => (changeSet.after_content), () => (item.content), () => (changeSet.after_excerpt), () => (''))
  ).trim();
  return {
    before_content: beforeContent,
    after_content: afterContent,
    before_lines: beforeContent ? beforeContent.split('\n') : [],
    after_lines: afterContent ? afterContent.split('\n') : [],
    changed: everyTruthy(() => (Boolean(beforeContent || afterContent)), () => (beforeContent !== afterContent)),
  };
}

function normalizeRevision(revision = {}, index = 0, previousRevision = {}) {
  const item = resolveObject(revision);
  const changeSet = resolveObject(item.change_set);
  const version = text(item.version);
  const activeTabKey = firstTruthy(() => (text(item.active_tab)), () => ('requirement'));
  const key = revisionKey(item, index);
  const content = String(firstTruthy(() => (item.content), () => ('')));
  return {
    key,
    revision_id: text(item.revision_id),
    artifact_id: text(item.artifact_id),
    version,
    label: version ? `版本 ${version}` : `版本 ${index + 1}`,
    active_tab: activeTabKey,
    source: text(item.source),
    source_kind: text(firstTruthy(() => (item.source_kind), () => (item.diff))),
    status: text(item.status),
    change_set: changeSet,
    change_summary: changeSummary(item),
    diff_preview: diffPreview(changeSet),
    diff_view: diffView({ previousRevision, revision: item }),
    content,
    created_at: text(firstTruthy(() => (item.created_at), () => (item.updated_at), () => (item.timestamp))),
    accepted_at: text(item.accepted_at),
    is_accepted: isAcceptedRevision(item),
    has_backend_rollback_capability: hasBackendRollbackCapability(item),
    is_latest: false,
    can_rollback: false,
    action: {
      action_key: 'rollback_artifact_revision',
      revision_key: key,
      revision_id: text(item.revision_id),
      active_tab: activeTabKey,
    },
  };
}

export function selectArtifactRevisionEntries({
  artifactDocument = {},
  tabKey = '',
  versions = [],
} = {}) {
  const latestRevision = resolveObject(resolveObject(artifactDocument).latest_revision);
  const latestKey = Object.keys(latestRevision).length > 0 ? revisionKey(latestRevision, -1) : '';
  const revisionsByKey = new Map();
  [...resolveArray(versions), latestRevision].forEach((revision, index) => {
    const item = resolveObject(revision);
    if (Object.keys(item).length === 0 || !revisionMatchesTab(item, tabKey)) {
      return;
    }
    const key = revisionKey(item, index);
    revisionsByKey.set(key, { index, item, key });
  });
  const orderedRevisions = [...revisionsByKey.values()].sort((left, right) => (
    revisionOrder(left.item, left.index) - revisionOrder(right.item, right.index)
  ));
  const previousRevisionByKey = new Map();
  orderedRevisions.forEach((entry, index) => {
    previousRevisionByKey.set(entry.key, orderedRevisions[index - 1]?.item || {});
  });
  const entriesByKey = new Map();
  revisionsByKey.forEach(({ index, item, key }) => {
    const entry = normalizeRevision(item, index, previousRevisionByKey.get(key));
    entriesByKey.set(key, {
      ...entry,
      is_latest: entry.key === latestKey,
    });
  });
  return [...entriesByKey.values()].map((entry) => {
    const canRollback = (
      (entry.has_backend_rollback_capability || entry.is_accepted)
      && !entry.is_latest
      && Boolean(text(entry.content))
    );
    return {
      ...entry,
      can_rollback: canRollback,
      action: {
        ...entry.action,
        enabled: canRollback,
        content: entry.content,
        label: entry.label,
      },
    };
  }).sort((left, right) => {
    if (left.is_latest !== right.is_latest) {
      return left.is_latest ? -1 : 1;
    }
    const leftVersion = Number(left.version);
    const rightVersion = Number(right.version);
    if (Number.isFinite(leftVersion) && Number.isFinite(rightVersion)) {
      return rightVersion - leftVersion;
    }
    return left.label.localeCompare(right.label);
  });
}

export function selectArtifactRevisionControls({
  revisionControls = {},
  revisionEntries = [],
} = {}) {
  const controls = resolveObject(revisionControls);
  const entries = resolveArray(revisionEntries);
  return {
    redo: revisionEntryFromControl(revisionControl(controls.redo), entries),
    undo: revisionEntryFromControl(revisionControl(controls.undo), entries),
  };
}

export default selectArtifactRevisionEntries;
